/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2026-01-09 14:59:05
 */
import './index.scss'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Popover, message } from 'antd'
import {
  useAppSelector,
  usePayment,
  useIsPaymentV2DownloadOnceRoute
} from '../../../app/hooks'
import type { RootState } from '../../../app/store'
import closeIcon from '../../../images/btn_close_info_window.png'
import contactUsIcon from '../../../images/svip-contactus.png'
import rightsTitleImage from '../../../images/payment/rights-title.png'
import rightsImage from '../../../images/payment/rights.png'
import sensors from '../../../utils/sensors'
import GoodsList from './GoodsList'
import PaymentArea from './PaymentArea'
import { GoodsItem } from './types'
import CONFIG from '../../../constants/config'
import ContactUS from '../ContactUS'
import SignalPayment from './SinglePayment'
import paymentConfig from '../../../constants/payment'
import wanganqiangQrCode from '../../../images/service-qrcode/wanganqiang.png'
import { ExclamationCircleOutlined } from '@ant-design/icons'

const { paymentType } = paymentConfig

const PaymentV2DownloadOnceContent = () => {
  const payment = usePayment()
  const group = useAppSelector((state: any) => state.group)
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const customerConfig = useAppSelector(
    (state: any) => state.group.customerConfig
  )
  const [selectedGoods, setSelectedGoods] = useState<GoodsItem | null>(null)

  const [batchDownloadGoodsMsg, setBatchDownloadGoodsMsg] = useState<{
    id: string
    price: string
    content: any
  } | null>(null)

  const photoState = useAppSelector((state) => state.photo)
  const checkedPhotos = photoState.checkedPhotos
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  // photov2 选中的照片（暂时兼容，未来将完全使用新版状态）
  const contentCheckedPhotos = useAppSelector(
    (state: RootState) => state.photosByContent?.checkedPhotos
  )
  const contentUncheckedPhotos = useAppSelector(
    (state: RootState) => state.photosByContent?.unCheckedPhotos
  )
  const contentCheckAll = useAppSelector(
    (state: RootState) => state.photosByContent?.checkAll
  )
  const contentPhotosNum = useAppSelector(
    (state: RootState) => state.photosByContent?.photosNum || 0
  )
  const isVip = useAppSelector((state: any) => state.group.isVip)
  const sourceDetail = useAppSelector(
    (state: any) => state.main.paymentStatus?.sourceDetail
  )

  const limitInfo = useAppSelector((state) => state.groupLimit.limitInfo)
  // photov2 选中的照片数量（暂时兼容，未来将完全使用新版状态）
  const contentCheckedNum = useMemo(() => {
    if (!contentCheckedPhotos && !contentCheckAll) return 0
    if (contentCheckAll) {
      const uncheckedSize = contentUncheckedPhotos?.size ?? 0
      return Math.max(0, contentPhotosNum - uncheckedSize)
    }
    return contentCheckedPhotos?.size ?? 0
  }, [
    contentCheckedPhotos,
    contentCheckAll,
    contentPhotosNum,
    contentUncheckedPhotos
  ])

  useEffect(() => {
    if (payment.paymentStatus.visible && group.photo_agg_type) {
      sensors.track('web_get_action_workgroup_buy_customer_vip_tips', {
        groupID,
        actionType: 'showPop'
      })
      Modal.info({
        centered: true,
        title: '温馨提示',
        content: (
          <>
            <p style={{ marginBottom: '0.5em' }}>
              您的团队正在使用{customerConfig.noun}
              {customerConfig.verb}功能
            </p>
            <p>建议在手机上购买该功能的专属会员</p>
          </>
        ),
        okText: '我知道了',
        onOk: () => {
          sensors.track('web_get_action_workgroup_buy_customer_vip_tips', {
            groupID,
            actionType: 'clickKnow'
          })
        }
      })
    }
  }, [payment.paymentStatus.visible, group.photo_agg_type, groupID])

  const handleSensorClickEvent = useCallback(
    (clickItem, goods = null) => {
      const _goods = goods || selectedGoods
      const pageType = sourceDetail === 'downloadOnce' ? 'payOnce' : 'groupVIP'
      sensors.track('web_click_page_workgroup_open_vip', {
        clickItem: clickItem,
        groupID,
        vipType: _goods?.content.vipType,
        vipLevel:
          _goods?.content.vipKind === CONFIG.VIP_KIND.VVIP
            ? 'premiumVip'
            : _goods?.content.vipKind === CONFIG.VIP_KIND.SVIP
              ? 'superVip'
              : 'generalVip',
        bool1: payment.paymentStatus.isFromToView,
        pageType: pageType,
        source_detail: sourceDetail,
        track_sign: 'avip.b4363.action.web_click_page_workgroup_open_vip',
        requirement_id: 232
      })
    },
    [groupID, selectedGoods, payment, sourceDetail]
  )

  const handleClose = useCallback(
    (isSensor = false) => {
      if (isSensor) {
        handleSensorClickEvent('close')
      }
      payment.close()
    },
    [groupID, handleSensorClickEvent]
  )

  const handleChangeGoods = useCallback((goods) => {
    setSelectedGoods(goods)
  }, [])

  useEffect(() => {
    handleGetGoodsList()
  }, [])

  const handleGetGoodsList = () => {
    if (isVip) return
    // 设置单次批量下载商品数据
    setBatchDownloadGoodsMsg({
      id: '1879461449567334400',
      price: '0.1',
      content: {
        vipType: paymentType.batchDownload
      }
    })
  }

  /**
   * 开通团队会员弹窗
   * */
  const paymentGroupContent = () => {
    return (
      <div className="body">
        <div className="left">
          <div className="payment-group-warpper">
            <div className="payment-group">
              {group && !group.avatar && (
                <div
                  className="payment-portrait"
                  style={{ backgroundColor: group.color }}
                >
                  {group.groupName.substr(0, 2)}
                </div>
              )}
              {group && group.avatar && (
                <div
                  className="payment-portrait"
                  style={{
                    backgroundImage: `url(${group.avatar})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat'
                  }}
                ></div>
              )}
              <div className="payment-info">
                <div className="payment-groupName">{group.groupName}</div>
                <div className="payment-desc">一人付费，全部成员可享权益</div>
              </div>
            </div>
            <div className="payment-tip">
              <ExclamationCircleOutlined style={{ marginRight: 4 }} />
              您开通的会员仅针对您当前选择的团队，多团队开通请联系客服
            </div>
          </div>
          <div className="rights-compare-wrapper">
            <img className="rights-title" src={rightsTitleImage} alt="" />
            <div className="rights-list">
              <img className="rights" src={rightsImage} alt="" />
            </div>
          </div>
        </div>
        <div className="right">
          <div className="period-tip">
            <ExclamationCircleOutlined style={{ marginRight: 4 }} />
            所有版本需按年付费
            {selectedGoods?.content?.vipKind === 2 && (
              <ContactUS fromPage="超级会员-web端">
                <Popover
                  content={
                    <img style={{ width: 240 }} src={wanganqiangQrCode} />
                  }
                  placement="rightBottom"
                >
                  <img
                    src={contactUsIcon}
                    className="contact-us"
                    onClick={() => {
                      handleSensorClickEvent('contactUs')
                    }}
                  />
                </Popover>
              </ContactUS>
            )}
          </div>
          <GoodsList
            selectedGoods={selectedGoods}
            onChange={handleChangeGoods}
            sensorClickEvent={handleSensorClickEvent}
          />
          <PaymentArea
            selectedGoods={selectedGoods}
            onClose={handleClose}
            sensorClickEvent={handleSensorClickEvent}
          />
          <div className="payment-agreement">
            <span>开通前请查阅</span>
            <div
              onClick={() => {
                handleSensorClickEvent('vipAgreement')
                window.open(
                  'https://www.xhey.top/x-vip_contract?app-hide-actions=1&version=2.2'
                )
              }}
            >
              《会员协议》
            </div>
            <div
              onClick={() => {
                handleSensorClickEvent('autoPurchaseAgreement')
                window.open(
                  'https://www.xhey.top/x-period_payment?app-hide-actions=1&version=2.2'
                )
              }}
            >
              《自动续费服务协议》
            </div>
          </div>
        </div>
      </div>
    )
  }

  const paymentModalContent = () => {
    if (
      sourceDetail === 'downloadOnce' &&
      (Object.keys(checkedPhotos || {}).length ||
        checkAll ||
        contentCheckedNum > 0) &&
      batchDownloadGoodsMsg &&
      !limitInfo?.photoViewLimit?.exist
    ) {
      return (
        <SignalPayment
          goodsMsg={batchDownloadGoodsMsg as any}
          onClose={handleClose}
          sensorClickEvent={handleSensorClickEvent}
        />
      )
    }
    return paymentGroupContent()
  }

  if (!payment.paymentStatus.visible) {
    return null
  }

  return (
    <div className="payment-v2">
      <div className="payment-modal">
        <div className="closeIconDiv">
          <img src={closeIcon} onClick={() => handleClose(true)} />
        </div>
        <div className="modal-content">{paymentModalContent()}</div>
      </div>
    </div>
  )
}

/** 仅按张下载（downloadOnce）时挂载，避免与 VipPaymentModal 并存时重复跑副作用 */
const Payment = () => {
  const isDownloadOnceRoute = useIsPaymentV2DownloadOnceRoute()
  if (!isDownloadOnceRoute) return null
  return <PaymentV2DownloadOnceContent />
}

export default Payment
